import React, { createContext, useState, useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

// Cognito Hosted UI endpoints using implicit grant
const LOGIN_URL =
  "https://actadiurna.auth.eu-west-2.amazoncognito.com/login" +
  "?client_id=4bi8nq055s0vrr43s3qc76p8jk" +
  "&response_type=token" +
  "&scope=email+openid+profile" +
  "&redirect_uri=https%3A%2F%2Factadiurna.org.uk%2F";

const LOGOUT_URL =
  "https://actadiurna.auth.eu-west-2.amazoncognito.com/logout" +
  "?client_id=4bi8nq055s0vrr43s3qc76p8jk" +
  "&logout_uri=https%3A%2F%2Factadiurna.org.uk%2F";

// User types
export const USER_TYPES = {
  ANONYMOUS: 'anonymous',
  READER: 'reader',
  JOURNALIST: 'journalist',
  ADMIN: 'admin'
};

// Helper to decode the JWT payload
function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

export const AuthContext = createContext({
  user: null,
  userType: USER_TYPES.ANONYMOUS,
  initialized: false,
  login: () => {},
  logout: () => {},
  getIdToken: () => null,
  updateUserProfile: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(USER_TYPES.ANONYMOUS);
  const [initialized, setInitialized] = useState(false);
  const [idToken, setIdToken] = useState(null);

  // Function to determine user type based on claims or attributes
  const determineUserType = (decoded) => {
    // Check for Cognito groups
    if (decoded["cognito:groups"]) {
      if (decoded["cognito:groups"].includes("admins")) {
        return USER_TYPES.ADMIN;
      }
      
      if (decoded["cognito:groups"].includes("journalists")) {
        return USER_TYPES.JOURNALIST;
      }
      
      if (decoded["cognito:groups"].includes("readers")) {
        return USER_TYPES.READER;
      }
    }
    
    // If logged in but not in any specific group, default to reader
    if (decoded["cognito:username"]) {
      return USER_TYPES.READER;
    }
    
    // Default to anonymous
    return USER_TYPES.ANONYMOUS;
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for token in URL hash (from Cognito redirect)
        const hash = window.location.hash.substr(1);
        const params = new URLSearchParams(hash);
        const urlToken = params.get("id_token");
        
        if (urlToken) {
          localStorage.setItem("id_token", urlToken);
          const decoded = decodeJwt(urlToken);
          const type = determineUserType(decoded);
          
          setUser({
            username: decoded["cognito:username"],
            email: decoded.email,
            name: decoded.name || decoded["cognito:username"],
            // Initialize profile fields
            bio: "",
            avatar: "",
            social: {},
            preferences: {},
            // Store raw claims for reference
            claims: decoded
          });
          
          setUserType(type);
          setIdToken(urlToken);
          window.history.replaceState({}, "", window.location.pathname);
        } else {
          // Check for stored token
          const storedToken = localStorage.getItem("id_token");
          if (storedToken) {
            const decoded = decodeJwt(storedToken);
            const type = determineUserType(decoded);
            
            setUser({
              username: decoded["cognito:username"],
              email: decoded.email,
              name: decoded.name || decoded["cognito:username"],
              bio: "",
              avatar: "",
              social: {},
              preferences: {},
              claims: decoded
            });
            
            setUserType(type);
            setIdToken(storedToken);
          } else {
            // Try to get session from Amplify
            try {
              const { tokens } = await fetchAuthSession();
              if (tokens) {
                const idToken = tokens.idToken.toString();
                localStorage.setItem("id_token", idToken);
                const decoded = decodeJwt(idToken);
                const type = determineUserType(decoded);
                
                setUser({
                  username: decoded["cognito:username"],
                  email: decoded.email,
                  name: decoded.name || decoded["cognito:username"],
                  bio: "",
                  avatar: "",
                  social: {},
                  preferences: {},
                  claims: decoded
                });
                
                setUserType(type);
                setIdToken(idToken);
              }
            } catch (error) {
              console.log("No active auth session");
              setUserType(USER_TYPES.ANONYMOUS);
            }
          }
        }
      } catch (error) {
        console.error("Auth error:", error);
        setUserType(USER_TYPES.ANONYMOUS);
      } finally {
        setInitialized(true);
      }
    };

    checkAuth();
  }, []);

  const login = () => {
    window.location.href = LOGIN_URL;
  };

  const logout = () => {
    localStorage.removeItem("id_token");
    setUser(null);
    setUserType(USER_TYPES.ANONYMOUS);
    setIdToken(null);
    window.location.href = LOGOUT_URL;
  };

  const getIdToken = () => idToken;
  
  // Function to update user profile information
  const updateUserProfile = (profileData) => {
    if (!user) return;
    
    // In a real app, you'd make an API call to update the profile
    // For now, we'll just update the local state
    setUser({
      ...user,
      ...profileData
    });
    
    // TODO: Sync with backend when API is available
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        userType, 
        initialized, 
        login, 
        logout, 
        getIdToken,
        updateUserProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}